import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useFormikContext } from 'formik'

import getDiscountCodeInfo, { DiscountInfo } from 'core/application/account/discountCodeInfo'
import SubscriptionFrequency from 'core/application/adminSignUp/common/subscriptionFrequency'
import { ProductIds } from 'core/application/commons/config'
import getSubscriptionFees, { SubscriptionFeesResponse } from 'core/application/subscriptions/getSubscriptionFees'
import getSubscriptionPlansForPackage, {
  SubscriptionPlansForPackageResponse,
} from 'core/application/subscriptions/getSubscriptionPlansForPackage'

import useCurrentProduct from 'common/hooks/useCurrentProduct'

import { Col, DefaultCheckbox, Radio, Row, Skeleton } from 'common/components'
import { MAX_QSEHRA_EMPLOYEES } from 'common/constants'
import { useQueryRequest } from 'common/hooks'
import { formatMoney } from 'common/moneyFormatter'

import pdf from 'assets/docs/PeopleKeepServicesAgreement.pdf'

import { InputNumber } from '../dataEntry/formik'

type SubscriptionFeesProps = {
  selectedSubscriptionPlan: SubscriptionPlansForPackageResponse
  serviceAgreement: boolean
  subscriptionPackageCode: string
  discountCode: string
  hideServiceAgreement?: boolean
  onSubscriptionPlanChange: (arg: SubscriptionPlansForPackageResponse) => void
  noPlatformFee?: boolean
}

const SubscriptionTypes = ({
  selectedSubscriptionPlan,
  serviceAgreement,
  discountCode,
  subscriptionPackageCode,
  hideServiceAgreement = false,
  noPlatformFee = false,
  onSubscriptionPlanChange,
}: SubscriptionFeesProps) => {
  const { t: translate } = useTranslation()
  const product = useCurrentProduct()
  const monthNumber = 12
  const [subscriptionPlanFees] = useQueryRequest<SubscriptionPlansForPackageResponse[]>(null, () =>
    getSubscriptionPlansForPackage(subscriptionPackageCode),
  )
  const { values } = useFormikContext<{ seatNumber: number }>()
  const { seatNumber } = values

  const currentSeatNumber =
    seatNumber < selectedSubscriptionPlan?.minimumSeats ? selectedSubscriptionPlan?.minimumSeats : seatNumber

  const [fees, isLoadingFees, sendGetPerksSubscriptionFeesRequest] = useQueryRequest<SubscriptionFeesResponse>(null)

  const [discount] = useQueryRequest<DiscountInfo>(null, () => getDiscountCodeInfo(discountCode))

  const lineThroughClass = noPlatformFee ? 'line-through text-decoration-red' : ''

  useEffect(() => {
    if (subscriptionPlanFees && subscriptionPlanFees.length > 0) {
      if (subscriptionPlanFees.length == 1) {
        onSubscriptionPlanChange(subscriptionPlanFees[0])
      } else {
        onSubscriptionPlanChange(subscriptionPlanFees[1])
      }
    }
  }, [subscriptionPlanFees])

  useEffect(() => {
    ;(async () => {
      if (selectedSubscriptionPlan) {
        await sendGetPerksSubscriptionFeesRequest(() =>
          getSubscriptionFees(selectedSubscriptionPlan.code, selectedSubscriptionPlan.productId, currentSeatNumber),
        )
      }
    })()
  }, [selectedSubscriptionPlan, currentSeatNumber])

  return (
    <>
      <Row justify="center" className="mt-8 mb-8">
        {selectedSubscriptionPlan != null && (
          <Col xs={24}>
            <Radio.Group
              name="code"
              defaultValue={selectedSubscriptionPlan.code}
              onRadioChange={(e) => {
                const found = subscriptionPlanFees.find((x) => x.code == e.target.value)
                onSubscriptionPlanChange(found)
              }}
            >
              <Row gutter={10}>
                {subscriptionPlanFees?.map((item, index) => (
                  <Col key={`${index}`} xs={12}>
                    {item.frequency.id === 1 ? (
                      <Radio.Button className="white-large" value={item.code}>
                        <div className="pb-16">
                          <p className="radio-button-title">
                            {translate('admin-signup.subscription.subscription-fees.annual')}
                          </p>
                          <ul className="pt-16">
                            <li>
                              <p className="mb-2 flex">
                                <div className="mr-4">
                                  {translate('admin-signup.subscription.subscription-fees.platform-fee')}
                                </div>
                                <div className={lineThroughClass}>
                                  <b> ${item.monthlyFee / monthNumber}</b>/
                                  {translate('admin-signup.subscription.subscription-fees.mo')}
                                </div>
                              </p>
                              <small className="primary-color mb-4">
                                {translate('admin-signup.subscription.subscription-fees.annual-fee-hint')}
                              </small>
                            </li>
                            <li>
                              <p className="pt-4 mb-0">
                                {translate('admin-signup.subscription.subscription-fees.seat-fee')} *{' '}
                                <b> ${item.employeeFee / monthNumber}</b>/
                                {translate('admin-signup.subscription.subscription-fees.mo')}
                              </p>
                              <small className="primary-color">
                                {translate('admin-signup.subscription.subscription-fees.seat-fee-hint')}
                              </small>
                            </li>
                          </ul>
                          <small>{translate('admin-signup.subscription.subscription-fees.annual-fee-reminder')}</small>
                        </div>
                      </Radio.Button>
                    ) : (
                      <Radio.Button className="white-large" value={item.code}>
                        <div style={{ height: '200px' }} className="pb-16">
                          <p className="radio-button-title">
                            {translate('admin-signup.subscription.subscription-fees.monthly')}
                          </p>
                          <ul className="pt-16">
                            <li>
                              <p className="mb-8 flex">
                                <div className="mr-4">
                                  {translate('admin-signup.subscription.subscription-fees.platform-fee')}
                                </div>
                                <div className={lineThroughClass}>
                                  <b> ${item.monthlyFee}</b>/
                                  {translate('admin-signup.subscription.subscription-fees.mo')}
                                </div>
                              </p>
                            </li>
                            <li>
                              <p>
                                {translate('admin-signup.subscription.subscription-fees.seat-fee')} *{' '}
                                <b> ${item.employeeFee}</b>/
                                {translate('admin-signup.subscription.subscription-fees.mo')}
                              </p>
                            </li>
                          </ul>
                        </div>
                      </Radio.Button>
                    )}
                  </Col>
                ))}
              </Row>
            </Radio.Group>
          </Col>
        )}
      </Row>
      {selectedSubscriptionPlan && !hideServiceAgreement && (
        <div className="mb-16">
          <div className="mb-8">
            {product.productId === ProductIds.IchraId
              ? 'How many of your W-2 employees will be eligible to participate in the ICHRA?'
              : product.productId === ProductIds.Perks
              ? 'How many participants will you add to the benefit?'
              : 'How many of your W-2 employees will be eligible to participate?'}
          </div>
          <InputNumber name="seatNumber" className="max-w-400 w-100" hideError={true} min={0} />
          {product.productId === ProductIds.QsehraId && seatNumber > MAX_QSEHRA_EMPLOYEES && (
            <div className="color-yellow-7 font-12 mb-4">
              {translate('admin-signup.subscription.subscription-fees.seats-label-1')}
            </div>
          )}
          {seatNumber < selectedSubscriptionPlan.minimumSeats && (
            <div className="color-yellow-7 font-12 mb-4">
              {translate('admin-signup.subscription.subscription-fees.seats-max-number')
                .replace('[NUMBER]', selectedSubscriptionPlan.minimumSeats.toString())
                .replace('[NUMBER]', selectedSubscriptionPlan.minimumSeats.toString())}
            </div>
          )}
          {product.productId === ProductIds.QsehraId && (
            <div className="flex font-12 mb-4">
              <div className="color-gray-7 mr-4">
                {translate('admin-signup.subscription.subscription-fees.seats-label-2')}
              </div>
              <a href="https://www.peoplekeep.com/blog/what-are-the-w2-requirements-for-a-qsehra" target="_blank">
                Learn more.
              </a>
            </div>
          )}

          <div className="color-gray-7 font-12 mb-4">
            {product.productId === ProductIds.IchraId ? (
              <>
                *Note: to comply with federal regulations, you must enroll all qualifying employees into your ICHRA so
                we can be provide them with proper legal notice of their benefit offering.{' '}
                <a
                  target="_blank"
                  href={
                    'https://peoplekeephelpichra.zendesk.com/hc/en-us/articles/13825251499671-Who-is-eligible-to-be-enrolled-in-your-ICHRA-'
                  }
                >
                  Learn more
                </a>
                . You can add other non-eligible users to your account at a later step.
              </>
            ) : (
              <>{translate('admin-signup.subscription.subscription-fees.seats-label-3')}</>
            )}
          </div>
        </div>
      )}
      {fees && (
        <>
          <Skeleton active loading={isLoadingFees}>
            <Row justify="center">
              <Col span={24}>
                <div className="card">
                  <div className="inner-padding">
                    <h4>{translate('admin-signup.subscription.subscription-fees.todays-charge-label')}</h4>
                  </div>

                  <div className="border-top-bottom inner-padding">
                    <Row justify={'space-between'} className={'mb-16'}>
                      <Col span={12}>{translate('admin-signup.subscription.subscription-fees.one-time-setup-fee')}</Col>
                      <Col span={12} className={'text-right'}>
                        {formatMoney(discount ? fees.setupFee - discount.amount : fees.setupFee)}
                      </Col>
                    </Row>

                    <Row justify={'space-between'} className={'mb-16'}>
                      <Col span={18}>
                        <span>
                          {fees.frequency === SubscriptionFrequency.Annual
                            ? translate('admin-signup.subscription.subscription-fees.annual-platform-fee-label')
                            : translate('admin-signup.subscription.subscription-fees.monthly-platform-fee-label')}
                        </span>

                        {fees.frequency === SubscriptionFrequency.Annual && (
                          <label className="annual-platform-fee">
                            (
                            {subscriptionPlanFees.filter((x) => x.frequency.id === SubscriptionFrequency.Annual)[0]
                              .monthlyFee / 12}{' '}
                            x 12 months)
                          </label>
                        )}

                        {fees.frequency === SubscriptionFrequency.Annual && (
                          <div className="annual-platform-fee-hint mt-8">
                            {translate('admin-signup.subscription.subscription-fees.annual-platform-fee-hint')}
                          </div>
                        )}
                      </Col>

                      <Col span={6} className={'text-right'}>
                        {formatMoney(
                          fees.frequency === SubscriptionFrequency.Monthly ? fees.proratedMonthlyFee : fees.monthlyFee,
                        )}

                        {fees.frequency === SubscriptionFrequency.Monthly && (
                          <div className="monthly-platform-fee-hint">
                            {translate('admin-signup.subscription.subscription-fees.monthly-platform-fee-hint')}
                          </div>
                        )}
                      </Col>
                    </Row>

                    <Row justify={'space-between'} className={'mb-16'}>
                      <Col span={18}>
                        {selectedSubscriptionPlan.frequency.id === SubscriptionFrequency.Annual ? (
                          <>
                            <span>{translate('admin-signup.subscription.minimum-seat')}</span>
                            <span className="gray-6">{`( $${fees.employeeFee / 12} x ${
                              fees.seatNumber
                            } seats) x 12 months`}</span>

                            {product.productId === ProductIds.Perks ? (
                              <div className="annual-platform-fee-hint mt-8">
                                {translate('admin-signup.subscription.subscription-fees.annual-seat-fee-hint')}
                              </div>
                            ) : product.productId === ProductIds.QsehraId ? (
                              <div className="annual-platform-fee-qshera-hint mt-8">
                                {translate('admin-signup.subscription.subscription-fees.annual-seat-fee-qshera-hint')}
                              </div>
                            ) : (
                              <div></div>
                            )}
                          </>
                        ) : (
                          <>
                            <span>{translate('admin-signup.subscription.minimum-seat')}</span>
                            <span className="gray-6">{`$${fees.employeeFee} x ${fees.seatNumber} seats`}</span>
                          </>
                        )}
                      </Col>

                      <Col span={6} className={'text-right'}>
                        {formatMoney(
                          fees.frequency == SubscriptionFrequency.Monthly
                            ? fees.seatNumber * fees.proratedEmployeeFee
                            : fees.seatNumber * fees.employeeFee,
                        )}

                        {fees.frequency === SubscriptionFrequency.Monthly && (
                          <div className="monthly-platform-fee-hint">
                            {translate('admin-signup.subscription.subscription-fees.monthly-platform-fee-hint')}
                          </div>
                        )}
                      </Col>
                    </Row>
                  </div>

                  <div className="inner-padding">
                    <h4 className="inline ">{translate('admin-signup.subscription.subscription-fees.total-label')}</h4>

                    <span className="weight-600 float-right">
                      {formatMoney(discount ? fees.todaysTotal - discount.amount : fees.todaysTotal)}
                    </span>
                  </div>
                </div>
              </Col>
            </Row>
          </Skeleton>
        </>
      )}
      {noPlatformFee && (
        <div className="font-12">{translate('admin-signup.subscription.subscription-fees.no-platform-fee')}</div>
      )}
      {!hideServiceAgreement && (
        <Row justify="center" className="mt-8">
          <Col span={24}>
            <div className="card p16-24">
              <h5 className="inline">
                {translate('admin-signup.subscription.subscription-fees.service-agreement-label')}
              </h5>
              <a href={pdf} target="_blank" className="float-right underline primary-color" rel="noopener noreferrer">
                {translate('general.view')}
              </a>
            </div>

            <div className="pt-8">
              <DefaultCheckbox name="serviceAgreement" className="no-select" defaultChecked={serviceAgreement}>
                {translate('admin-signup.subscription.subscription-fees.service-agreement-check')}
              </DefaultCheckbox>
            </div>
          </Col>
        </Row>
      )}
    </>
  )
}

export default SubscriptionTypes
